import axios from 'axios';
import mockOnetService from './mockOnetService';

const ONET_API_BASE_URL = '/api/onet';
const ONET_API_KEY = import.meta.env.VITE_ONET_API_KEY || 'fx2xs-Hls2s-VudRB-R3BGR';

class OnetApiService {
  constructor() {
    this.baseURL = ONET_API_BASE_URL;
    this.apiKey = ONET_API_KEY;
  }

  /**
   * Get occupation details by SOC code
   * @param {string} socCode - SOC occupation code
   * @param {string} contentType - Type of content to retrieve
   */
  async getOccupationDetails(socCode, contentType = 'details') {
    try {
      const response = await axios.get(`${this.baseURL}/mnm/careers/${socCode}`, {
        params: {
          content: contentType
        },
        headers: {
          'X-API-Key': this.apiKey
        }
      });

      return { success: true, data: response.data };
    } catch (error) {
      console.error(`Error fetching O*NET data for SOC Code ${socCode}:`, error);
      console.log('Falling back to mock data...');
      
      // Fall back to mock service
      return await mockOnetService.getOccupationDetails(socCode, contentType);
    }
  }

  /**
   * Search occupations by keyword
   * @param {string} keyword - Search keyword
   * @param {number} limit - Maximum number of results
   */
  async searchOccupations(keyword, limit = 20) {
    try {
      // O*NET API has a maximum limit of 20 per request, so we need to paginate
      const maxPerRequest = 20;
      const totalRequests = Math.ceil(limit / maxPerRequest);
      let allResults = [];
      
      for (let i = 0; i < totalRequests; i++) {
        const start = i * maxPerRequest + 1;
        const end = Math.min((i + 1) * maxPerRequest, limit);
        
        const response = await axios.get(`${this.baseURL}/mnm/search`, {
          params: {
            keyword: keyword,
            start: start,
            end: end
          },
          headers: {
            'X-API-Key': this.apiKey
          }
        });
        
        if (response.data && response.data.career) {
          allResults = allResults.concat(response.data.career);
        }
        
        // If we got fewer results than requested, we've reached the end
        if (!response.data.career || response.data.career.length < maxPerRequest) {
          break;
        }
      }
      
      return { success: true, data: { career: allResults, total: allResults.length } };
    } catch (error) {
      console.error(`Error searching O*NET occupations for keyword "${keyword}":`, error);
      console.log('Falling back to mock data...');
      
      // Fall back to mock service
      return await mockOnetService.searchOccupations(keyword, limit);
    }
  }

  /**
   * Get occupation skills and abilities
   * @param {string} socCode - SOC occupation code
   */
  async getOccupationSkills(socCode) {
    try {
      const response = await axios.get(`${this.baseURL}/careers/${socCode}/skills`, {
        headers: {
          'X-API-Key': this.apiKey
        }
      });

      return { success: true, data: response.data };
    } catch (error) {
      console.error(`Error fetching skills for SOC Code ${socCode}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get occupation knowledge requirements
   * @param {string} socCode - SOC occupation code
   */
  async getOccupationKnowledge(socCode) {
    try {
      const response = await axios.get(`${this.baseURL}/careers/${socCode}/knowledge`, {
        headers: {
          'X-API-Key': this.apiKey
        }
      });

      return { success: true, data: response.data };
    } catch (error) {
      console.error(`Error fetching knowledge for SOC Code ${socCode}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get occupation work activities
   * @param {string} socCode - SOC occupation code
   */
  async getOccupationWorkActivities(socCode) {
    try {
      const response = await axios.get(`${this.baseURL}/careers/${socCode}/work_activities`, {
        headers: {
          'X-API-Key': this.apiKey
        }
      });

      return { success: true, data: response.data };
    } catch (error) {
      console.error(`Error fetching work activities for SOC Code ${socCode}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get occupation work context
   * @param {string} socCode - SOC occupation code
   */
  async getOccupationWorkContext(socCode) {
    try {
      const response = await axios.get(`${this.baseURL}/careers/${socCode}/work_context`, {
        headers: {
          'X-API-Key': this.apiKey
        }
      });

      return { success: true, data: response.data };
    } catch (error) {
      console.error(`Error fetching work context for SOC Code ${socCode}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get occupation education requirements
   * @param {string} socCode - SOC occupation code
   */
  async getOccupationEducation(socCode) {
    try {
      const response = await axios.get(`${this.baseURL}/careers/${socCode}/education`, {
        headers: {
          'X-API-Key': this.apiKey
        }
      });

      return { success: true, data: response.data };
    } catch (error) {
      console.error(`Error fetching education for SOC Code ${socCode}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get occupation interests (Holland codes)
   * @param {string} socCode - SOC occupation code
   */
  async getOccupationInterests(socCode) {
    try {
      const response = await axios.get(`${this.baseURL}/careers/${socCode}/interests`, {
        headers: {
          'X-API-Key': this.apiKey
        }
      });

      return { success: true, data: response.data };
    } catch (error) {
      console.error(`Error fetching interests for SOC Code ${socCode}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get occupation work values
   * @param {string} socCode - SOC occupation code
   */
  async getOccupationWorkValues(socCode) {
    try {
      const response = await axios.get(`${this.baseURL}/careers/${socCode}/work_values`, {
        headers: {
          'X-API-Key': this.apiKey
        }
      });

      return { success: true, data: response.data };
    } catch (error) {
      console.error(`Error fetching work values for SOC Code ${socCode}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get occupation job outlook and projections
   * @param {string} socCode - SOC occupation code
   */
  async getOccupationJobOutlook(socCode) {
    try {
      const response = await axios.get(`${this.baseURL}/careers/${socCode}/job_outlook`, {
        headers: {
          'X-API-Key': this.apiKey
        }
      });

      return { success: true, data: response.data };
    } catch (error) {
      console.error(`Error fetching job outlook for SOC Code ${socCode}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get comprehensive occupation data
   * @param {string} socCode - SOC occupation code
   */
  async getComprehensiveOccupationData(socCode) {
    try {
      const [
        details,
        skills,
        knowledge,
        workActivities,
        workContext,
        education,
        interests,
        workValues,
        jobOutlook
      ] = await Promise.all([
        this.getOccupationDetails(socCode),
        this.getOccupationSkills(socCode),
        this.getOccupationKnowledge(socCode),
        this.getOccupationWorkActivities(socCode),
        this.getOccupationWorkContext(socCode),
        this.getOccupationEducation(socCode),
        this.getOccupationInterests(socCode),
        this.getOccupationWorkValues(socCode),
        this.getOccupationJobOutlook(socCode)
      ]);

      return {
        success: true,
        data: {
          details: details.data,
          skills: skills.data,
          knowledge: knowledge.data,
          workActivities: workActivities.data,
          workContext: workContext.data,
          education: education.data,
          interests: interests.data,
          workValues: workValues.data,
          jobOutlook: jobOutlook.data,
          lastUpdated: new Date().toISOString()
        }
      };
    } catch (error) {
      console.error(`Error fetching comprehensive data for SOC Code ${socCode}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get related occupations
   * @param {string} socCode - SOC occupation code
   */
  async getRelatedOccupations(socCode) {
    try {
      const response = await axios.get(`${this.baseURL}/careers/${socCode}/related`, {
        headers: {
          'X-API-Key': this.apiKey
        }
      });

      return { success: true, data: response.data };
    } catch (error) {
      console.error(`Error fetching related occupations for SOC Code ${socCode}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get occupation categories
   */
  async getOccupationCategories() {
    try {
      const response = await axios.get(`${this.baseURL}/mnm/categories`, {
        headers: {
          'X-API-Key': this.apiKey
        }
      });

      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching occupation categories:', error);
      console.log('Falling back to mock data...');
      
      // Fall back to mock service
      return await mockOnetService.getOccupationCategories();
    }
  }

  /**
   * Get occupations by category
   * @param {string} categoryId - Category ID
   */
  async getOccupationsByCategory(categoryId) {
    try {
      const response = await axios.get(`${this.baseURL}/categories/${categoryId}/occupations`, {
        headers: {
          'X-API-Key': this.apiKey
        }
      });

      return { success: true, data: response.data };
    } catch (error) {
      console.error(`Error fetching occupations for category ${categoryId}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Process O*NET data for database storage
   * @param {Object} onetData - Raw O*NET data
   */
  processOnetDataForDatabase(onetData) {
    const details = onetData.details?.career || {};
    const skills = onetData.skills?.skills || [];
    const knowledge = onetData.knowledge?.knowledge || [];
    const workActivities = onetData.workActivities?.work_activities || [];
    const workContext = onetData.workContext?.work_context || [];
    const education = onetData.education?.education || {};
    const interests = onetData.interests?.interests || [];
    const workValues = onetData.workValues?.work_values || [];
    const jobOutlook = onetData.jobOutlook?.job_outlook || {};

    return {
      // Basic details
      title: details.title || '',
      description: details.description || '',
      soc_code: details.soc_code || '',
      
      // Skills and abilities
      skills_required: this.extractSkills(skills),
      knowledge_areas: this.extractKnowledge(knowledge),
      work_activities: this.extractWorkActivities(workActivities),
      
      // Work context
      work_context: this.extractWorkContext(workContext),
      
      // Education and requirements
      education_level: this.mapEducationLevel(education),
      experience_level: this.mapExperienceLevel(details),
      
      // Interests and values
      interests: this.extractInterests(interests),
      work_values: this.extractWorkValues(workValues),
      
      // Job outlook
      growth_outlook: this.mapJobOutlook(jobOutlook),
      job_growth_rate: this.extractJobGrowthRate(jobOutlook),
      
      // Remote work compatibility
      remote_friendly: this.assessRemoteWorkCompatibility(workContext),
      
      // Industry tags
      industry_tags: this.extractIndustryTags(details),
      
      // Metadata
      onet_last_updated: new Date().toISOString(),
      data_source: 'ONET'
    };
  }

  /**
   * Extract skills from O*NET data
   * @param {Array} skills - Skills array from O*NET
   */
  extractSkills(skills) {
    return skills
      .filter(skill => skill.importance && skill.level)
      .map(skill => ({
        name: skill.name,
        importance: skill.importance,
        level: skill.level
      }))
      .sort((a, b) => b.importance - a.importance)
      .slice(0, 20); // Top 20 skills
  }

  /**
   * Extract knowledge areas from O*NET data
   * @param {Array} knowledge - Knowledge array from O*NET
   */
  extractKnowledge(knowledge) {
    return knowledge
      .filter(k => k.importance && k.level)
      .map(k => ({
        name: k.name,
        importance: k.importance,
        level: k.level
      }))
      .sort((a, b) => b.importance - a.importance)
      .slice(0, 15); // Top 15 knowledge areas
  }

  /**
   * Extract work activities from O*NET data
   * @param {Array} workActivities - Work activities array from O*NET
   */
  extractWorkActivities(workActivities) {
    return workActivities
      .filter(activity => activity.importance && activity.level)
      .map(activity => ({
        name: activity.name,
        importance: activity.importance,
        level: activity.level
      }))
      .sort((a, b) => b.importance - a.importance)
      .slice(0, 15); // Top 15 work activities
  }

  /**
   * Extract work context from O*NET data
   * @param {Array} workContext - Work context array from O*NET
   */
  extractWorkContext(workContext) {
    return workContext
      .filter(context => context.importance && context.level)
      .map(context => ({
        name: context.name,
        importance: context.importance,
        level: context.level
      }))
      .sort((a, b) => b.importance - a.importance);
  }

  /**
   * Map education level from O*NET data
   * @param {Object} education - Education object from O*NET
   */
  mapEducationLevel(education) {
    const level = education.education_level;
    if (!level) return 'unknown';
    
    const levelMap = {
      '1': 'high_school',
      '2': 'some_college',
      '3': 'associates',
      '4': 'bachelors',
      '5': 'masters',
      '6': 'doctoral',
      '7': 'professional'
    };
    
    return levelMap[level] || 'unknown';
  }

  /**
   * Map experience level from O*NET data
   * @param {Object} details - Details object from O*NET
   */
  mapExperienceLevel(details) {
    const experience = details.experience_required;
    if (!experience) return 'unknown';
    
    const experienceMap = {
      '1': 'entry',
      '2': 'junior',
      '3': 'mid',
      '4': 'senior',
      '5': 'expert'
    };
    
    return experienceMap[experience] || 'unknown';
  }

  /**
   * Extract interests from O*NET data
   * @param {Array} interests - Interests array from O*NET
   */
  extractInterests(interests) {
    return interests
      .filter(interest => interest.importance && interest.level)
      .map(interest => ({
        name: interest.name,
        importance: interest.importance,
        level: interest.level
      }))
      .sort((a, b) => b.importance - a.importance);
  }

  /**
   * Extract work values from O*NET data
   * @param {Array} workValues - Work values array from O*NET
   */
  extractWorkValues(workValues) {
    return workValues
      .filter(value => value.importance && value.level)
      .map(value => ({
        name: value.name,
        importance: value.importance,
        level: value.level
      }))
      .sort((a, b) => b.importance - a.importance);
  }

  /**
   * Map job outlook from O*NET data
   * @param {Object} jobOutlook - Job outlook object from O*NET
   */
  mapJobOutlook(jobOutlook) {
    const growth = jobOutlook.growth_rate;
    if (!growth) return 'unknown';
    
    if (growth > 10) return 'very_high';
    if (growth > 5) return 'high';
    if (growth > 0) return 'moderate';
    if (growth > -5) return 'stable';
    return 'declining';
  }

  /**
   * Extract job growth rate from O*NET data
   * @param {Object} jobOutlook - Job outlook object from O*NET
   */
  extractJobGrowthRate(jobOutlook) {
    return jobOutlook.growth_rate || null;
  }

  /**
   * Assess remote work compatibility
   * @param {Array} workContext - Work context array from O*NET
   */
  assessRemoteWorkCompatibility(workContext) {
    const remoteIndicators = workContext.filter(context => 
      context.name.toLowerCase().includes('remote') ||
      context.name.toLowerCase().includes('telework') ||
      context.name.toLowerCase().includes('work from home')
    );
    
    return remoteIndicators.length > 0;
  }

  /**
   * Extract industry tags from O*NET data
   * @param {Object} details - Details object from O*NET
   */
  extractIndustryTags(details) {
    const industries = details.industries || [];
    return industries.map(industry => industry.name);
  }
}

export default new OnetApiService();